'use client'

import {
  cloneElement,
  type ElementType,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { cn } from '@/lib/utils'

export interface FillTextProps {
  children: ReactNode
  className?: string
  as?: ElementType
}

export function FillText({ children, className, as: Component = 'span' }: FillTextProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLElement>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const calculateAndApplyScale = () => {
    if (!textRef.current || !containerRef.current) return

    const container = containerRef.current
    const text = textRef.current

    text.style.fontSize = '100px'
    text.style.display = 'inline-block'
    text.style.whiteSpace = 'nowrap'

    const textWidth = text.offsetWidth
    const containerWidth = container.offsetWidth

    if (textWidth > 0 && containerWidth > 0) {
      const scale = containerWidth / textWidth
      text.style.fontSize = `${100 * scale}px`
    }
  }

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    const handleFontLoad = () => {
      calculateAndApplyScale()
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(handleFontLoad)
    } else {
      handleFontLoad()
    }

    if (containerRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(calculateAndApplyScale)
      })
      resizeObserverRef.current.observe(containerRef.current)
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [children])

  useEffect(() => {
    const handleResize = () => calculateAndApplyScale()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isElement = isValidElement(children)

  return (
    <div ref={containerRef} className='w-full'>
      {isElement ? (
        cloneElement(children as ReactElement<any>, {
          ref: textRef,
          style: {
            ...(children as ReactElement<any>).props.style,
            display: 'inline-block',
            whiteSpace: 'nowrap',
          },
        })
      ) : (
        <Component ref={textRef} className={cn('inline-block whitespace-nowrap', className)}>
          {children}
        </Component>
      )}
    </div>
  )
}
